import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { generateStory, getGenerationStatus } from '../../api/stories';
import useFocusOnMount from '../../hooks/useFocusOnMount';
import audioManager from '../../lib/audioManager';
import CloudBand from '../../components/common/CloudBand';
import { useLearner } from '../../hooks/useLearner';
import s from './GeneratingPage.module.css';

// ─── Loading image pool ───────────────────────────────────────────────────────
// To add more images: drop a new WebP in public/assets/story-loading/ and
// append its path here. No other code changes needed.
const LOADING_IMAGES = [
  '/assets/story-loading/story-loading-2.webp',
  '/assets/story-loading/story-loading-3.webp',
  '/assets/story-loading/story-loading-4.webp',
  '/assets/story-loading/story-loading-5.webp',
  '/assets/story-loading/story-loading-6.webp',
  '/assets/story-loading/story-loading-7.webp',
];

// ─── Progressive feedback thresholds (seconds elapsed) ────────────────────────
const REASSURE_AFTER = 30;       // tagline switches to "still crafting…"
const LONGER_NOTICE_AFTER = 60;  // calm "taking longer than usual" notice appears
const CANCEL_BUTTON_AFTER = 120; // user can cancel and retry

// ─── Polling (BP-ASYNC-GEN-02) ────────────────────────────────────────────────
const POLL_INTERVAL_MS = 4000;
const MAX_CONSECUTIVE_POLL_ERRORS = 5;

export default function GeneratingPage() {
  const { t } = useTranslation();
  const mainRef = useFocusOnMount();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { learnerId, topic } = location.state || {};
  const [error, setError] = useState(null); // { type: 'generic' | 'cancelled', message?: string } | null
  const [showCalibrationModal, setShowCalibrationModal] = useState(false);
  const [pendingStoryId, setPendingStoryId] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [retryKey, setRetryKey] = useState(0);
  const elapsedTimerRef = useRef(null);
  const pollTimerRef = useRef(null);
  // FP-CLIENT-SUBMIT-GUARD-01: caches the in-flight generate POST per attempt
  // (keyed by retryKey) so re-renders / StrictMode double-mounts can't fire a
  // second POST /stories/generate. A Try Again bumps retryKey → a fresh, single
  // POST. Set once before the network call; never reset on cleanup.
  const submitRef = useRef({ key: null, promise: null });
  const { data: activeLearner } = useLearner();
  const childName = activeLearner?.name || 'your child';

  // Pick one image randomly on mount; hold it for the full loading duration
  const [loadingImg] = useState(
    () => LOADING_IMAGES[Math.floor(Math.random() * LOADING_IMAGES.length)]
  );

  // Story-generation ambient (audio-system.md). Loops while we wait; the unmount
  // cleanup guarantees it stops whether the user completes, cancels, or leaves.
  useEffect(() => {
    audioManager.playAmbient();
    return () => audioManager.stopAmbient();
  }, []);

  // Stop the loop the moment an error surfaces (the component stays mounted on
  // the error screen, so the unmount cleanup alone wouldn't catch this).
  useEffect(() => {
    if (error) audioManager.stopAmbient();
  }, [error]);

  // Submit + poll (re-runs when retryKey bumps)
  useEffect(() => {
    if (!learnerId || !topic) {
      navigate('/', { replace: true });
      return;
    }

    let cancelled = false;

    function clearPoll() {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    }

    function handleComplete(jobResult) {
      // Generation finished — stop the ambient loop and fire the "it's ready!"
      // chime (audio-system.md → story-ready resolves the story-generating loop).
      audioManager.stopAmbient();
      audioManager.playSFX('story-ready');
      queryClient.invalidateQueries({ queryKey: ['childStories'] });
      queryClient.invalidateQueries({ queryKey: ['learnerProfile'] });
      const storyId = jobResult.story_id;
      if (!storyId) {
        setError({ type: 'generic', message: null });
        return;
      }
      if (jobResult.is_calibration) {
        setPendingStoryId(storyId);
        setShowCalibrationModal(true);
      } else {
        navigate(`/story/read/${storyId}`, { replace: true });
      }
    }

    function startPolling(jobId) {
      let consecutiveErrors = 0;
      pollTimerRef.current = setInterval(async () => {
        try {
          const result = await getGenerationStatus(jobId);
          if (cancelled) return;
          consecutiveErrors = 0;
          if (result.status === 'complete') {
            clearPoll();
            handleComplete(result);
          } else if (result.status === 'failed') {
            clearPoll();
            setError({ type: 'generic', message: result.error || null });
          }
          // pending / processing → keep polling
        } catch (pollErr) {
          if (cancelled) return;
          consecutiveErrors += 1;
          if (consecutiveErrors >= MAX_CONSECUTIVE_POLL_ERRORS) {
            clearPoll();
            setError({ type: 'generic', message: pollErr.message || null });
          }
        }
      }, POLL_INTERVAL_MS);
    }

    elapsedTimerRef.current = setInterval(() => setElapsed((sec) => sec + 1), 1000);

    // Fire POST /stories/generate exactly once per attempt. Re-renders and
    // StrictMode's mount→unmount→mount reuse the same in-flight promise instead
    // of issuing a second POST; each effect run still attaches its own
    // cancelled-guarded handler, so the live mount starts polling even when the
    // first (StrictMode) mount has been torn down. A new attempt only happens
    // via retryKey (Try Again), which can only fire from the settled error
    // screen — so two submits never overlap.
    if (submitRef.current.key !== retryKey) {
      submitRef.current = {
        key: retryKey,
        promise: generateStory({ learnerId, topic }),
      };
    }
    submitRef.current.promise
      .then((result) => {
        const jobId = result.job_id;
        // The daily slot is claimed server-side the moment a job_id comes back
        // (rpc_claim_story_slot, atomic at request time). Refresh the map's
        // Create gate immediately so daily_limit_used flips without waiting for a
        // natural refetch — this is the back-out case: the user may have already
        // navigated back to the map, so fire BEFORE the cancelled check (the
        // unmounted run would otherwise skip it). queryClient is stable and
        // invalidateQueries is safe + idempotent post-unmount.
        if (jobId) {
          queryClient.invalidateQueries({ queryKey: ['childStories'] });
        }
        if (cancelled) return;
        if (!jobId) {
          setError({ type: 'generic', message: null });
          return;
        }
        startPolling(jobId);
      })
      .catch((err) => {
        if (cancelled) return;
        // 429 (daily limit), 503, or network error from the submit itself.
        // No timeout case — backend returns 202 inside ~2s.
        setError({ type: 'generic', message: err.message || null });
      });

    return () => {
      cancelled = true;
      if (elapsedTimerRef.current) {
        clearInterval(elapsedTimerRef.current);
        elapsedTimerRef.current = null;
      }
      clearPoll();
    };
  }, [learnerId, topic, navigate, queryClient, retryKey]);

  function handleRetry() {
    setError(null);
    setElapsed(0);
    setRetryKey((k) => k + 1);
  }

  function handleCancel() {
    // BP-ASYNC-GEN-02: the job keeps running on the backend and the story
    // will appear in the bookshelf when it lands. The user just leaves.
    if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    if (elapsedTimerRef.current) clearInterval(elapsedTimerRef.current);
    navigate('/', { replace: true });
  }

  if (error) {
    const titleKey =
      error.type === 'cancelled'
        ? 'generating.cancelledTitle'
        : 'generating.errorTitle';
    const messageText =
      error.type === 'cancelled'
        ? t('generating.cancelledMessage')
        : error.message || t('generating.errorMessageGeneric');

    return (
      <div className={s.errorState} role="alert">
        <p className={s.errorTitle}>{t(titleKey)}</p>
        <p className={s.errorMessage}>{messageText}</p>
        <div className={s.errorActions}>
          <button
            type="button"
            className={s.errorBtn}
            onClick={handleRetry}
          >
            {t('generating.tryAgain')}
          </button>
          <button
            type="button"
            className={s.errorBtnSecondary}
            onClick={() => navigate('/', { replace: true })}
          >
            {t('common.goHome')}
          </button>
        </div>
      </div>
    );
  }

  const taglineKey =
    elapsed >= REASSURE_AFTER
      ? 'generating.loadingMessageStill'
      : 'generating.loadingMessage';

  return (
    <main className={s.page} ref={mainRef} tabIndex={-1} aria-live="polite">
      <h1 className={s.srOnly}>{t('generating.srTitle')}</h1>

      {/* Hero: teal zone with mascot + cloud band at bottom */}
      <div className={s.hero}>
        <div className={s.imageWrap} aria-hidden="true">
          <img
            src={loadingImg}
            alt=""
            className={s.loadingImg}
          />
        </div>
        <CloudBand variant="cream" className={s.cloudBand} />
      </div>

      {/* Body: cream zone with tagline + progressive feedback */}
      <div className={s.body}>
        <p className={s.tagline}>{t(taglineKey)}</p>

        {elapsed >= LONGER_NOTICE_AFTER && !showCalibrationModal && (
          <p className={s.longerNotice}>{t('generating.longerNotice')}</p>
        )}

        {elapsed >= CANCEL_BUTTON_AFTER && !showCalibrationModal && (
          <button
            type="button"
            className={s.cancelBtn}
            onClick={handleCancel}
          >
            {t('generating.cancel')}
          </button>
        )}
      </div>

      {/* Calibration intro modal */}
      {showCalibrationModal && (
        <div className={s.modalOverlay} role="dialog" aria-modal="true" aria-labelledby="calibModal-heading">
          <div className={s.modalCard}>
            <p className={s.modalHeading} id="calibModal-heading">
              {t('generating.meetTheCrew')}
            </p>
            <p className={s.modalBody}>
              {t('generating.firstStoryExplain', { childName })}
            </p>
            <button
              type="button"
              className={s.modalBtn}
              onClick={() => navigate(`/story/read/${pendingStoryId}`, { replace: true })}
            >
              {t('generating.startAdventure')}
            </button>
          </div>
        </div>
      )}
    </main>
  );
}
